import React from 'react';

import { Box } from '@opendoor/atoms/box';

import { AppleAuthRedirectButton } from '../../apple/AppleAuthRedirectButton';
import { useAnalyticsPrefix } from '../../AuthContext';
import type { ProviderPreference } from '../../clientProviderPreference';
import { GoogleAuthRedirectButton } from '../../google/GoogleAuthRedirectButton';
import type { LastUsedMethod } from '../../lastUsedWebAuthMethod';
import { type RedirectAuthButtonProps, RedirectAuthGroup } from '../../oauth/RedirectAuthButton';
import { useAccountForm } from '../AccountFormContext';
import { LastUsedAffordance } from './LastUsedAffordance';

/**
 * COMPONENT segment of this module's `APP-COMPONENT-ACTION` analytics names.
 * The method stack belongs to the account form's entry surface, so it shares
 * `EmailModule`'s component rather than naming itself after the provider.
 */
const ANALYTICS_COMPONENT = 'account-form';

// Hoisted so the affordance receives a stable array rather than a fresh one
// per render.
const GOOGLE_METHODS: readonly LastUsedMethod[] = ['google'];
const APPLE_METHODS: readonly LastUsedMethod[] = ['apple'];

interface ProviderEntry {
  /** Which last-used methods this button serves, for the badge to match on. */
  methods: readonly LastUsedMethod[];
  Button: React.FC<RedirectAuthButtonProps>;
  /** Omitted takes the button's own default. */
  variant?: RedirectAuthButtonProps['variant'];
}

/**
 * The provider buttons in default stack order — Google above Apple.
 *
 * A list rather than fixed JSX because the order is a decision: the seller's own
 * last-used method comes first, and failing that their client's likelier account.
 */
const PROVIDERS: readonly ProviderEntry[] = [
  { methods: GOOGLE_METHODS, Button: GoogleAuthRedirectButton },
  { methods: APPLE_METHODS, Button: AppleAuthRedirectButton, variant: 'secondaryInverse' },
];

interface ProviderOrderOptions {
  /**
   * The method this seller last signed in with, when one is known. Outranks
   * `clientPreferred`, because a method they actually used beats a guess from
   * their browser.
   */
  lastUsed: LastUsedMethod | undefined;
  /** The provider the rendering client suggests, when the surface asked for it. */
  clientPreferred: ProviderPreference | undefined;
}

/**
 * The hoisted provider first, everything else in declared order.
 *
 * Resolved during render, never in an effect: painting the default order and
 * then reordering would move live buttons under a cursor already travelling
 * toward one of them.
 */
const orderProviders = ({
  lastUsed,
  clientPreferred,
}: ProviderOrderOptions): readonly ProviderEntry[] => {
  // Ordered by precedence, and each signal can name a method with no button
  // here — `email-password` is a LastUsedMethod — so a signal that hoists
  // nothing falls through to the next rather than settling the order.
  for (const method of [lastUsed, clientPreferred]) {
    const hoisted = method === undefined ? undefined : hoist(method);
    if (hoisted !== undefined) {
      return hoisted;
    }
  }
  return PROVIDERS;
};

const hoist = (method: LastUsedMethod): readonly ProviderEntry[] | undefined => {
  const serves = (entry: ProviderEntry): boolean => entry.methods.includes(method);
  const matched = PROVIDERS.filter(serves);
  if (matched.length === 0) {
    return undefined;
  }
  return [...matched, ...PROVIDERS.filter((entry) => !serves(entry))];
};

/**
 * The V2 social-auth stack: full-width Google (secondary/outline with the brand
 * icon) and Apple (solid near-black) buttons, rendered above the email field in
 * the default composition. Both render unconditionally — SIWA has shipped, so
 * Apple needs no gate of its own here. Usable standalone under a bare
 * `AccountFormProvider` or as `AccountFormV2`'s social module.
 *
 * The stack order follows the returning seller's last-used provider, then their
 * client's likelier account, so the declared Google-above-Apple order is a
 * default rather than a fixed layout.
 */
export const SocialAuthModule: React.FC = () => {
  const { redirectTo, setFormError, lastUsedMethod, clientPreferredProvider } = useAccountForm();
  const analyticsPrefix = useAnalyticsPrefix();
  const buttonAnalyticsPrefix = `${analyticsPrefix}-${ANALYTICS_COMPONENT}`;
  const providers = orderProviders({
    lastUsed: lastUsedMethod,
    clientPreferred: clientPreferredProvider,
  });

  return (
    // A redirect that came back with `?auth_error=` is a failure of this form,
    // so its copy goes to the form's one alert region — which the composition
    // renders above the method stack — rather than inline under a button.
    <RedirectAuthGroup publishError={setFormError}>
      <Box className="flex w-full flex-col gap-4">
        {providers.map(({ methods, Button, variant }) => (
          <LastUsedAffordance
            analyticsComponent={ANALYTICS_COMPONENT}
            key={methods.join()}
            methods={methods}
          >
            {(association) => (
              <Button
                analyticsPrefix={buttonAnalyticsPrefix}
                redirectTo={redirectTo}
                variant={variant}
                fullWidth
                {...association}
              />
            )}
          </LastUsedAffordance>
        ))}
      </Box>
    </RedirectAuthGroup>
  );
};
